import Joi from 'joi';
import api from '../api';
import { paginationSchema } from '../cortizol/schemas';

/**
 * Post to get an auth token.
 * @param {object} [credentials]
 * @param {string} credentials.username
 * @param {string} credentials.password
 * @param {"password"|"refresh_token"} credentials.grantType
 * @returns {Promise<AuthObject>}
 */
export const postAuthToken = async (credentials) => {
  const result = await api.post('/identity-access/v1-0/pools/admin/token', credentials, { withCredentials: true });

  try {
    const schema = Joi.object({
      accessToken: Joi.string().required(),
    });
    await schema.validateAsync(result.data, { allowUnknown: true });
  } catch (error) {
    throw new Error(`API response do not match AuthObject Schema : ${error.message}`);
  }

  return result.data;
};
/**
 * Get the connected user's permissions Map.
 * @returns {Promise<{data: Array<Permission>}>}
 */
export const getPermissionsMap = async () => {
  const result = await api.get('/identity-access/v1-0/permissions-map');

  try {
    const permissionSchema = Joi.object({
      microServiceId: Joi.string().required(),
      version: Joi.number().required(),
      permissions: Joi.array().items(Joi.string()).required(),
    });
    const schema = Joi.object({
      data: Joi.array().items(permissionSchema).required(),
    });
    await schema.validateAsync(result.data, { allowUnknown: true });
  } catch (error) {
    throw new Error(`API response do not match Permissions Map Schema : ${error.message}`);
  }

  return result.data;
};
/**
 * Get the list of admin users.
 * @param {object} queryParams
 * @param {number} queryParams.pageNumber
 * @param {number} queryParams.pageSize
 * @returns {Promise<{data: AdminUser[], pagination: Pagination}>}
 */
export const getAdminUsersList = async (queryParams) => {
  const result = await api.get('/identity-access/v1-0/pools/admin/users', { params: queryParams });

  try {
    const adminUsersListSchema = Joi.object({
      id: Joi.string().required(),
      poolId: Joi.string().required(),
      version: Joi.number().required(),
      emailAddress: Joi.string().required(),
      roleIds: Joi.array().items(Joi.string()).required(),
      isActive: Joi.boolean().required(),
      firstName: Joi.string().required(),
      lastName: Joi.string().required(),
      hasEmailAddressVerified: Joi.boolean().required(),
      creationTimestamp: Joi.number().required(),
    });
    const schema = Joi.object({
      data: Joi.array().items(adminUsersListSchema).required(),
      pagination: paginationSchema,
    });
    await schema.validateAsync(result.data, { allowUnknown: true });
  } catch (error) {
    throw new Error(`API response do not match Admin Users List Schema : ${error.message}`);
  }

  return result.data;
};
/**
 * Get the list of admin roles.
 * @param {object} queryParams
 * @param {number} queryParams.pageNumber
 * @param {number} queryParams.pageSize
 * @returns {Promise<{data: AdminRole[], pagination: Pagination}>}
 */
export const getAdminRolesList = async (queryParams) => {
  const result = await api.get('/identity-access/v1-0/pools/admin/roles', { params: queryParams });

  try {
    const rolesListSchema = Joi.object({
      id: Joi.string().required(),
      poolId: Joi.string().required(),
      version: Joi.number().integer().required(),
      name: Joi.string().required(),
      permissions: Joi.array().items(Joi.string()).required(),
    });
    const schema = Joi.object({
      data: Joi.array().items(rolesListSchema).required(),
      pagination: paginationSchema,
    });
    await schema.validateAsync(result.data, { allowUnknown: true });
  } catch (error) {
    throw new Error(`API response do not match Roles List Schema : ${error.message}`);
  }

  return result.data;
};
/**
 * Get an admin user by its ID.
 * @param {string} userId
 * @returns {Promise<AdminUser>}
 */
export const getAdminUser = async (userId) => {
  const result = await api.get(`/identity-access/v1-0/pools/admin/users/${userId}`);

  try {
    const userSchema = Joi.object({
      id: Joi.string().required(),
      poolId: Joi.string().required(),
      version: Joi.number().required(),
      emailAddress: Joi.string().required(),
      roleIds: Joi.array().items(Joi.string()).required(),
      isActive: Joi.boolean().required(),
      firstName: Joi.string().required(),
      lastName: Joi.string().required(),
      hasEmailAddressVerified: Joi.boolean().required(),
      creationTimestamp: Joi.number().required(),
    });
    await userSchema.validateAsync(result.data, { allowUnknown: true });
  } catch (error) {
    throw new Error(`API response do not match Admin User Schema : ${error.message}`);
  }

  return result.data;
};
/**
 * Post to create a user.
 * @param {object} user
 * @param {string} user.emailAddress
 * @param {string|null} user.password
 * @param {string|null} user.emailAddressConfirmationRedirectUrl
 * @param {string[]} user.roleIds
 * @param {string} user.firstName
 * @param {string} user.lastName
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postCreateUser = async (user) => api.post('/identity-access/v1-0/pools/admin/users', user);
/**
 * Put to update a user.
 * @param {string} userId
 * @param {object} user
 * @param {number} user.version
 * @param {string[]} user.roleIds
 * @param {string} user.firstName
 * @param {string} user.lastName
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const putUpdateAdminUser = async (userId, user) => api.put(`/identity-access/v1-0/pools/admin/users/${userId}`, user);
/**
 * Put to update an admin user email adress.
 * @param {string} userId
 * @param {object} user
 * @param {number} user.version
 * @param {string} user.emailAddress
 * @param {string} user.emailAddressConfirmationRedirectUrl
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const putUpdateAdminUserEmail = async (userId, user) => api
  .put(`/identity-access/v1-0/pools/admin/users/${userId}/email-address`, user);
/**
 * Post to activate an admin user.
 * @param {string} userId
 * @param {number} version
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postActivateAdminUser = async (userId, version) => api
  .post(`/identity-access/v1-0/pools/admin/users/${userId}:activate`, { version });
/**
 * Post to deactivate an admin user.
 * @param {string} userId
 * @param {number} version
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postDeactivateAdminUser = async (userId, version) => api
  .post(`/identity-access/v1-0/pools/admin/users/${userId}:deactivate`, { version });
/**
 * Post to reset the password of an admin user.
 * @param {object} data
 * @param {string} data.userEmailAddress
 * @param {string} data.passwordResetUrl
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postAdminUserPasswordReset = async (data) => api
  .post('/identity-access/v1-0/pools/admin/users:initialize-password-reset', data);
/**
 * Post to finalize the reset the password of an admin user.
 * @param {object} data
 * @param {string} data.token
 * @param {string} data.password
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postAdminUserPasswordResetFinalize = async (data) => api
  .post('/identity-access/v1-0/pools/admin/users:finalize-password-reset', data);
/**
 * Get to confirm email adresse.
 * @param {string} redirectUrl
 * @param {string} token
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const getConfirmEmailAddress = async (redirectUrl, token) => api
  .get('/identity-access/v1-0/pools/admin/users:confirm-email-address', { params: { redirectUrl, token } });
